import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";

export function CrudPage({ title, endpoint, columns, fields, testPrefix, defaults = {}, canWrite = true, transformItems = null }) {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/${endpoint}`, { params: q ? { q } : {} });
      setItems(res.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [endpoint, q]);

  useEffect(() => { load(); }, [load]);

  const openForm = (item) => {
    setEditing(item || null);
    const f = { ...defaults };
    if (item) fields.forEach((fd) => { f[fd.name] = item[fd.name] ?? defaults[fd.name] ?? ""; });
    setForm(f);
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {};
      fields.forEach((fd) => { if (fd.type !== "computed") payload[fd.name] = form[fd.name]; });
      if (editing) await api.put(`/${endpoint}/${editing.id}`, payload);
      else await api.post(`/${endpoint}`, payload);
      toast.success(t("save") + " OK");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/${endpoint}/${item.id}`);
      toast.success("OK");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid={`${testPrefix}-page`}>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight flex-1">{title}</h1>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid={`${testPrefix}-search-input`} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="pl-9 w-52" />
        </div>
        {canWrite && (
          <Button data-testid={`${testPrefix}-add-btn`} onClick={() => openForm(null)} className="bg-amber-600 hover:bg-amber-700 text-white">
            <Plus className="w-4 h-4 mr-1.5" /> {t("add")}
          </Button>
        )}
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                {columns.map((c) => <th key={c.key} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{c.label}</th>)}
                {canWrite && <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("actions")}</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-muted-foreground">{t("loading")}</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-muted-foreground">-</td></tr>
              ) : (transformItems ? transformItems(items) : items).map((item) => (
                <tr key={item._rowKey || item.id} data-testid={`${testPrefix}-row-${item.id}`} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                  {columns.map((c) => <td key={c.key} className="px-4 py-3">{c.render ? c.render(item) : item[c.key] ?? "-"}</td>)}
                  {canWrite && (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button data-testid={`${testPrefix}-edit-${item.id}`} onClick={() => openForm(item)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"><Pencil className="w-4 h-4" /></button>
                      <button data-testid={`${testPrefix}-delete-${item.id}`} onClick={() => remove(item)} className="p-1.5 rounded-md hover:bg-rose-50 dark:hover:bg-rose-950/40 text-muted-foreground hover:text-rose-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`${testPrefix}-form-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? t("edit") : t("add")} {title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {fields.map((fd) => (
              <div key={fd.name}>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{fd.label}</Label>
                {fd.type === "textarea" ? (
                  <Textarea data-testid={`field-${fd.name}`} value={form[fd.name] ?? ""} onChange={(e) => setForm({ ...form, [fd.name]: e.target.value })} className="mt-1.5" />
                ) : fd.type === "select" ? (
                  <Select value={String(form[fd.name] ?? "")} onValueChange={(v) => setForm({ ...form, [fd.name]: v })}>
                    <SelectTrigger data-testid={`field-${fd.name}`} className="mt-1.5"><SelectValue placeholder="..." /></SelectTrigger>
                    <SelectContent>
                      {(fd.options || []).map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : fd.type === "checkbox" ? (
                  <div className="mt-2 flex items-center gap-2">
                    <Switch data-testid={`field-${fd.name}`} checked={!!form[fd.name]} onCheckedChange={(v) => setForm({ ...form, [fd.name]: v })} />
                    <span className="text-sm text-muted-foreground">{form[fd.name] ? "✓" : "–"}</span>
                  </div>
                ) : fd.type === "computed" ? (
                  <div className="mt-1.5 px-3.5 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-sm font-mono font-bold text-amber-700 dark:text-amber-400" data-testid={`field-${fd.name}`}>
                    {fd.render ? fd.render(form) : "-"}
                  </div>
                ) : fd.type === "image" ? (
                  <div className="mt-1.5 space-y-2">
                    <Input data-testid={`field-${fd.name}`} value={form[fd.name] ?? ""} onChange={(e) => setForm({ ...form, [fd.name]: e.target.value })} placeholder="https://..." />
                    {form[fd.name] && <img src={form[fd.name]} alt="" className="h-20 w-20 rounded-lg object-cover border border-border" />}
                  </div>
                ) : (
                  <Input data-testid={`field-${fd.name}`} type={fd.type === "number" ? "number" : "text"} value={form[fd.name] ?? ""} onChange={(e) => setForm({ ...form, [fd.name]: fd.type === "number" ? Number(e.target.value) : e.target.value })} className="mt-1.5" />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid={`${testPrefix}-cancel-btn`}>{t("cancel")}</Button>
            <Button onClick={save} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid={`${testPrefix}-save-btn`}>{t("save")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
